import React from "react";
import { CalendarX2, Check, Pencil, Plus, Send, Trash2 } from "lucide-react";
import type { ITimesheet } from "../../types/timesheet.api.types";
import { CARD_CLASS } from "../../constants/dashboard";
import { projectColor } from "../../constants/timesheet";
import { SimpleTooltip } from "../common";
import { isPendingWorkflowStatus } from "../../utils/constants";
import {
  computeTaskHours,
  DAY_LABELS,
  formatHours,
  getDayStatusPill,
  isToday,
  type DraftTask,
} from "../../utils/timesheetWeek";

interface DayTimesheetCardProps {
  day: Date;
  entry?: ITimesheet;
  mode: "editable" | "locked";
  tasks: DraftTask[];
  isSubmitting?: boolean;
  isWeekOff?: boolean;
  /** Working hours for a full day; used for the day progress bar. */
  dayTargetHours?: number;
  onTasksChange: (tasks: DraftTask[]) => void;
  onDeleteDay?: () => void;
  onSubmitSingleDay?: () => void;
  onAddTask?: () => void;
  onEditTask?: (index: number) => void;
}

const DayTimesheetCard: React.FC<DayTimesheetCardProps> = ({
  day,
  entry,
  mode,
  tasks,
  isSubmitting = false,
  isWeekOff = false,
  dayTargetHours = 0,
  onTasksChange,
  onDeleteDay,
  onSubmitSingleDay,
  onAddTask,
  onEditTask,
}) => {
  const pill = getDayStatusPill(entry?.status, entry?.statusLabel);
  const today = isToday(day);

  const pillTooltip = (() => {
    const lines: React.ReactNode[] = [];
    if (pill.detail) {
      lines.push(<span key="detail">{pill.detail}</span>);
    }
    if (
      entry &&
      isPendingWorkflowStatus(entry.status) &&
      entry.currentApproverName
    ) {
      lines.push(
        <span key="approver" className="mt-1 block text-[10.5px] opacity-80">
          {`Awaiting approval from ${entry.currentApproverName}`}
        </span>,
      );
    }
    return lines.length > 0 ? lines : null;
  })();

  const dayHours =
    mode === "locked"
      ? entry?.totalHours ?? 0
      : tasks.reduce((sum, t) => sum + computeTaskHours(t), 0);
  const fillPct =
    dayTargetHours > 0 ? Math.min(100, Math.round((dayHours / dayTargetHours) * 100)) : 0;

  const removeTask = (index: number) => {
    onTasksChange(tasks.filter((_, i) => i !== index));
  };

  const renderTaskRow = (
    t: { name: string; project?: string; hours?: number; startTime?: string; endTime?: string },
    index: number,
    editable: boolean,
  ) => {
    const hours =
      typeof t.hours === "number" && t.hours > 0
        ? t.hours
        : computeTaskHours(t as DraftTask);

    return (
      <div
        key={`${t.name}-${index}`}
        className={`flex gap-2 rounded-[10px] bg-[var(--color-bg-hover)] px-2 py-2 ${
          editable && onEditTask ? "group cursor-pointer hover:bg-surface-muted transition-colors" : ""
        }`}
        onClick={editable && onEditTask ? () => onEditTask(index) : undefined}
        role={editable && onEditTask ? "button" : undefined}
      >
        <span
          className="w-[3px] flex-none rounded-[2px]"
          style={{ background: projectColor(t.project) }}
        />
        <div className="flex-1 min-w-0">
          <div className="text-body-sm font-bold text-text-primary whitespace-nowrap overflow-hidden text-ellipsis">
            {t.name}
          </div>
          <div className="text-caption text-text-tertiary whitespace-nowrap overflow-hidden text-ellipsis">
            {t.project || "—"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-none">
          <span className="text-caption font-bold text-text-strong whitespace-nowrap">
            {formatHours(hours)}
          </span>
          {editable && (
            <>
              <SimpleTooltip
                label="Edit task"
                side="top"
                tooltipClassName="bg-white text-[var(--color-text-primary)] border border-[var(--color-border-primary)] shadow-xl"
              >
                <span className="opacity-0 group-hover:opacity-100 text-[var(--color-layout-header-icon-secondary)] hover:text-primary-700 transition-opacity">
                  <Pencil className="w-3 h-3" />
                </span>
              </SimpleTooltip>
              <SimpleTooltip
                label="Delete task"
                side="top"
                tooltipClassName="bg-white text-[var(--color-text-primary)] border border-[var(--color-border-primary)] shadow-xl"
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTask(index);
                  }}
                  aria-label={`Remove task ${index + 1}`}
                  disabled={isSubmitting}
                  className="p-0.5 rounded text-[var(--color-layout-header-icon-secondary)] hover:text-[var(--color-error-dark)] transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </SimpleTooltip>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${CARD_CLASS} p-0 overflow-hidden flex flex-col h-[240px] relative transition-all`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border-tertiary">
        <div className="min-w-0 flex items-baseline gap-1.5">
          <span className={`text-label font-bold ${today ? "text-primary-700" : "text-text-primary"}`}>
            {DAY_LABELS[day.getDay()]}
          </span>
          <span className="text-label font-semibold text-text-tertiary">
            {String(day.getDate()).padStart(2, "0")}
          </span>
          {isWeekOff && (
            <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200">
              Week Off
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {mode === "editable" && entry?.status === "draft" && tasks.length > 0 && onSubmitSingleDay && (
            <SimpleTooltip
              label="Submit this day's timesheet"
              side="bottom"
              className="inline-block"
              tooltipClassName="bg-white text-[var(--color-text-primary)] border border-[var(--color-border-primary)] shadow-xl"
            >
              <button
                type="button"
                onClick={onSubmitSingleDay}
                disabled={isSubmitting}
                aria-label={`Submit ${DAY_LABELS[day.getDay()]} timesheet`}
                className="p-1 rounded-md text-primary-700 hover:bg-primary-700/10 hover:text-primary-800 transition-colors disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </SimpleTooltip>
          )}

          {mode === "editable" && entry?.status === "draft" && onDeleteDay && (
            <button
              type="button"
              onClick={onDeleteDay}
              aria-label={`Delete ${DAY_LABELS[day.getDay()]} timesheet`}
              className="p-1 rounded-md text-[var(--color-layout-header-icon-secondary)] hover:text-[var(--color-error-dark)] hover:bg-error-light transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}

          {pillTooltip ? (
            <SimpleTooltip
              label={pillTooltip}
              side="top"
              className="inline-block"
              tooltipClassName="bg-white text-[var(--color-text-primary)] border border-[var(--color-border-primary)] shadow-xl"
            >
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-caption font-bold whitespace-nowrap"
                style={{ background: pill.bg, color: pill.text }}
              >
                {pill.label}
              </span>
            </SimpleTooltip>
          ) : (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-caption font-bold whitespace-nowrap"
              style={{ background: pill.bg, color: pill.text }}
            >
              {pill.label}
            </span>
          )}
        </div>
      </div>

      {/* Day progress (Figma: mini bar + total) */}
      <div className="px-4 pt-3 pb-1">
        <div className="h-1.5 rounded-full bg-track overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-600 to-primary-700 transition-all duration-300"
            style={{ width: `${fillPct}%` }}
          />
        </div>
        <div className="text-caption font-bold text-text-strong mt-1">
          {formatHours(dayHours)}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 p-3 pt-1 flex flex-col gap-2 min-h-0">
        {mode === "locked" ? (
          entry && entry.tasks.length > 0 ? (
            <div className="flex-1 min-h-0 flex flex-col gap-2 overflow-y-auto overflow-x-hidden pr-1 scrollbar-thin scrollbar-thumb-[var(--color-layout-dropdown-badge-border)]">
              {entry.tasks.map((t, i) => renderTaskRow(t, i, false))}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-[var(--color-layout-header-icon-secondary)] py-4">
              <CalendarX2 className="w-[22px] h-[22px]" />
              <span className="text-caption font-semibold">No tasks yet</span>
            </div>
          )
        ) : tasks.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-[var(--color-layout-header-icon-secondary)] py-4">
            <CalendarX2 className="w-[22px] h-[22px]" />
            <span className="text-caption font-semibold">No tasks yet</span>
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0 flex flex-col gap-2 overflow-y-auto overflow-x-hidden pr-1 scrollbar-thin scrollbar-thumb-[var(--color-layout-dropdown-badge-border)]">
              {tasks.map((t, index) => renderTaskRow(t, index, true))}
            </div>

            {onAddTask && (
              <button
                type="button"
                onClick={onAddTask}
                disabled={isSubmitting}
                className="mt-1 h-9 shrink-0 flex items-center justify-center gap-1.5 border border-dashed border-[var(--color-border-input)] bg-transparent rounded-[10px] text-body-sm font-bold text-primary-700 hover:bg-primary-700/10 hover:border-primary-700 transition-all disabled:opacity-50"
              >
                <Plus className="w-3.5 h-3.5" />
                Add task
              </button>
            )}
          </>
        )}

        {mode === "editable" && tasks.length === 0 && onAddTask && (
          <button
            type="button"
            onClick={onAddTask}
            disabled={isSubmitting}
            className="mt-1 h-9 shrink-0 flex items-center justify-center gap-1.5 border border-dashed border-[var(--color-border-input)] bg-transparent rounded-[10px] text-body-sm font-bold text-primary-700 hover:bg-primary-700/10 hover:border-primary-700 transition-all disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Add task
          </button>
        )}
      </div>
    </div>
  );
};

export default DayTimesheetCard;
